import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactPaginate from "react-paginate";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import {
  IconPlus,
  IconChevronRight,
  IconChevronLeft,
  IconSearch,
  IconEdit,
  IconTrash,
  IconCheck,
  IconMinus,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Menu,
  ScrollArea,
  Select,
  Table,
  TableThead,
  TextInput,
  useMantineTheme,
  Checkbox,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useModal } from "../contexts/useModal";
import {
  UserGenerate,
  UserUpdate,
  WorkerUserGenerateToAdmin,
} from "../components/Modal";
import { INT_MAX, setActionLog } from "../utils/util";
import { useDynamicScroll } from "../utils/useDynamicScroll";
import {
  dangerActionProps,
  labels,
  primaryActionProps,
  searchActionProps,
  secondaryActionProps,
} from "../constants/uiActions";
import { useCanWrite } from "../utils/useCanWrite";

const Workers: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [length, setLength] = useState(15);
  const [pageNo, setPageNo] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);
  const _isSearch = useRef(false);
  const [isSearch, setIsSearch] = useState(false);
  const todayStr = dayjs().format("YYYY-MM-DD"); // KST 기준 오늘 날짜
  const [startDate, setStartDate] = useState<string>(todayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [permissionCodes, setPermissionCodes] = useState<
    { value: string; label: string }[]
  >([]);
  const _isAction = useRef(false);
  const [isAction, setIsAction] = useState(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] =
    useState<boolean>(false);
  const [userId, setUserId] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [role, setRole] = useState<string>("");

  const theme = useMantineTheme();
  const { showModal } = useModal();
  const lock1 = useRef(false);

  // 동적 스크롤 제어 (최소 높이 500px)
  useDynamicScroll({ minHeight: 500 });
  useEffect(() => {
    if (!lock1.current) {
      lock1.current = true;
      return;
    }
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      getList();
    }
  }, [length]);
  useEffect(() => {
    getList();
  }, [pageNo]);
  const getList = async () => {
    if (_isSearch.current) return;
    setSelectedRows([]);
    _isSearch.current = true;
    setIsSearch(true);

    try {
      const res = await axiosInstance.get("/api/v1/role/permission/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          workerYn: "Y",
        },
      });
      if (res.data.code === "200") {
        const mapped = res.data.data.list.map((item: any) => ({
          value: item.roles,
          label: item.roleName,
        }));
        const withDefault = [{ value: "", label: "전체" }, ...mapped];
        setPermissionCodes(withDefault);
      } else {
        setPermissionCodes([{ value: "", label: "전체" }]);
      }
    } catch (err) {
      setPermissionCodes([{ value: "", label: "전체" }]);
    }

    try {
      const res = await axiosInstance.get("/api/v1/user/list", {
        params: {
          currentPageNo: pageNo,
          recordsPerPage: length,
          workerYn: "Y",
          approvalYn: "Y",
          quitYn: "N",
          userId: userId || undefined,
          userName: userName || undefined,
          roles: role || undefined,
          startDate: startDate ? `${startDate} 00:00:00` : undefined,
          endDate: endDate ? `${endDate} 23:59:59` : undefined,
        },
      });
      if (res.data.code === "200") {
        const data = res.data as any;
        const r = data.data;
        const p = r.paging;
        setData(r.list || []);
        setTotal(p.totalRecordCount);
        setPageTotal(Math.ceil(p.totalRecordCount / length));
      } else {
        setData([]);
      }
    } catch (err) {
      toast.error(labels.searchFailed);
      setData([]);
    } finally {
      _isSearch.current = false;
      setIsSearch(false);
    }
  };

  const handleUserGenerate = async () => {
    if (!canWrite) return;
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "작업자 등록 버튼 클릭",
    });
    const flag = await showModal({
      component: WorkerUserGenerateToAdmin,
      props: {},
    });
    if (flag) {
      toast.success("등록이 완료되었습니다.");
      getList();
    }
  };

  const handleUserUpdate = async (row: any) => {
    if (!canWrite) return;
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "작업자 수정 버튼 클릭",
      content: row,
    });
    const flag = await showModal({
      component: UserUpdate,
      props: { row: row, workerYn: "Y" },
    });
    if (flag) {
      toast.success("수정이 완료되었습니다.");
      getList();
    }
  };

  // 체크박스 선택 관련 함수들
  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const handleSelectAll = (checked: boolean) => {
    setSelectedRows(checked ? data.map((row) => row.userNo) : []);
  };

  const handleSelectRow = (userNo: number, checked: boolean) => {
    setSelectedRows((prev) => {
      if (checked) {
        const updated = [...prev, userNo];
        return Array.from(new Set(updated)).sort((a, b) => a - b);
      } else {
        return prev.filter((id) => id !== userNo);
      }
    });
  };

  // 다중 수정 처리
  const handleBulkUpdate = async () => {
    if (!canWrite) return;
    if (selectedRows.length === 0) {
      toast.error("수정할 작업자를 선택해주세요");
      return;
    }
    if (selectedRows.length > 1) {
      toast.error("수정은 한 명씩만 가능합니다");
      return;
    }

    const selectedUser = data.find((user) => user.userNo === selectedRows[0]);
    if (selectedUser) {
      await handleUserUpdate(selectedUser);
    }
  };

  // 퇴사 처리
  const handleBulkDelete = async () => {
    if (!canWrite) return;
    if (selectedRows.length === 0) {
      toast.error("퇴사 처리할 작업자를 선택해주세요");
      return;
    }

    if (_isAction.current) return;
    _isAction.current = true;
    setIsAction(true);

    try {
      let successCount = 0;
      let failCount = 0;

      // 각 사용자마다 개별적으로 퇴사 처리
      for (const userNo of selectedRows) {
        const user = data.find((item) => item.userNo === userNo);
        if (!user) continue;

        try {
          const res = await axiosInstance.patch("/api/v1/user", {
            userNo: user.userNo,
            userId: user.userId,
            quitYn: "Y",
          });

          if (res.data.code === "200") {
            await setActionLog({
              eventType: "UPDATE",
              title: "작업자 퇴사 처리 완료",
              content: {
                userNo: user.userNo,
                userId: user.userId,
                userName: user.userName,
                quitYn: "Y",
              },
            });
            successCount++;
          } else {
            await setActionLog({
              eventType: "UPDATE",
              title: "작업자 퇴사 처리 실패",
              content: {
                userNo: user.userNo,
                userId: user.userId,
                userName: user.userName,
                quitYn: "Y",
                error: res.data.msg,
              },
            });
            failCount++;
          }
        } catch (err) {
          await setActionLog({
            eventType: "UPDATE",
            title: "작업자 퇴사 처리 실패",
            content: {
              userNo: user.userNo,
              userId: user.userId,
              userName: user.userName,
              quitYn: "Y",
              error: err,
            },
          });
          failCount++;
        }
      }

      // 결과 메시지 표시
      if (successCount > 0 && failCount === 0) {
        toast.success(`${successCount}명의 퇴사 처리가 완료되었습니다.`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`${successCount}명 성공, ${failCount}명 실패했습니다.`);
      } else {
        toast.error("퇴사 처리에 실패하였습니다.");
      }

      getList();
    } catch (err) {
      toast.error("퇴사 처리 중 오류가 발생했습니다.");
    } finally {
      _isAction.current = false;
      setIsAction(false);
    }
  };

  const cancelIsAction = () => {
    _isAction.current = false;
    setIsAction(false);
  };

  const rows = data.map((row, idx) => {
    const isSelected = selectedRows.includes(row.userNo);
    return (
      <Table.Tr
        key={row.userNo}
        onClick={() => {
          handleSelectRow(row.userNo, !isSelected);
        }}
        bg={
          isSelected
            ? "var(--mantine-color-blue-light)"
            : hoveredRow === idx
            ? "#f0f4fa"
            : undefined
        }
        onMouseEnter={() => setHoveredRow(idx)}
        onMouseLeave={() => setHoveredRow(null)}
        fw={isSelected ? "700" : undefined}
        style={{ cursor: "pointer" }}
      >
        <Table.Td p={"4px 12px"}>
          <Checkbox
            aria-label="Select row"
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => {
              handleSelectRow(row.userNo, event.currentTarget.checked);
            }}
          />
        </Table.Td>
        <Table.Td p={"4px 12px"}>{row.userNo}</Table.Td>
        <Table.Td p={"4px 12px"}>{row.userId}</Table.Td>
        <Table.Td p={"4px 12px"}>{row.userName}</Table.Td>
        <Table.Td p={"4px 12px"}>
          {permissionCodes.find((item) => item.value === row.roles)?.label ||
            ""}
        </Table.Td>
        <Table.Td p={"4px 12px"} ta={"left"}>
          {row.lastLoginDate}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div
      style={{
        height: "100%",
        background: "#fafbfc",
        borderRadius: "12px",
        padding: "0.75rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "500px", // 최소 높이 보장
      }}
    >
      <Flex align={"center"} justify={"space-between"} pb={"1rem"}>
        <Box fz={"1.75rem"} fw={700}>
          {menuTitle}
        </Box>
        <Flex align={"flex-end"} gap={"xs"}>
          <Button
            leftSection={<IconPlus size={20} stroke={1.5} />}
            onClick={() => {
              handleUserGenerate();
            }}
            {...primaryActionProps}
            disabled={!canWrite}
          >
            {labels.register}
          </Button>
          <Button
            leftSection={<IconEdit size={20} stroke={1.5} />}
            onClick={handleBulkUpdate}
            {...secondaryActionProps}
            disabled={!canWrite || isSearch || isAction || selectedRows.length !== 1}
          >
            {labels.edit}
          </Button>
          <Button
            leftSection={<IconTrash size={20} stroke={1.5} />}
            onClick={handleBulkDelete}
            {...dangerActionProps}
            disabled={!canWrite || isSearch || isAction || selectedRows.length === 0}
          >
            {labels.delete}
          </Button>
        </Flex>
      </Flex>

      {/* 기본 검색 영역 */}
      <Flex align={"flex-end"} gap={"xs"} pb={"1rem"}>
        <TextInput
          label="아이디"
          placeholder="아이디를 입력하세요"
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
          }}
        />
        <TextInput
          label="이름"
          placeholder="이름을 입력하세요"
          value={userName}
          onChange={(e) => {
            setUserName(e.target.value);
          }}
        />
        <Select
          label="권한"
          data={permissionCodes}
          defaultValue={permissionCodes[0]?.value || ""}
          value={role}
          allowDeselect={false}
          onChange={(val) => {
            setRole(val || "");
          }}
        />
        <Button
          leftSection={<IconSearch size={20} stroke={1.5} />}
          onClick={getList}
          disabled={isSearch}
          {...searchActionProps}
        >
          {labels.search}
        </Button>
        <Button
          leftSection={
            isAdvancedSearchOpen ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )
          }
          onClick={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
          variant="outline"
          color="gray"
          radius="sm"
          size="sm"
        >
          {isAdvancedSearchOpen
            ? labels.advancedSearchClose
            : labels.advancedSearchOpen}
        </Button>
      </Flex>

      {/* 고급 검색 영역 (아코디언) */}
      {isAdvancedSearchOpen && (
        <Flex align={"flex-end"} gap={"xs"} pb={"1rem"} wrap="wrap">
          <DateInput
            label={labels.searchStart}
            placeholder="날짜를 선택하세요"
            valueFormat="YYYY-MM-DD"
            value={startDate}
            onChange={(val) => {
              setStartDate(val || todayStr);
            }}
            nextIcon={<IconChevronRight size={16} />}
            previousIcon={<IconChevronLeft size={16} />}
            renderDay={(dateStr) => {
              const dateObj = new Date(dateStr); // 문자열 → Date 객체
              const day = dateObj.getDay();

              const style: React.CSSProperties = {
                color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
              };

              return <div style={style}>{dateObj.getDate()}</div>;
            }}
          />
          <DateInput
            label={labels.searchEnd}
            placeholder="날짜를 선택하세요"
            valueFormat="YYYY-MM-DD"
            value={endDate}
            onChange={(val) => {
              let newEnd = val || todayStr;
              if (new Date(newEnd) < new Date(startDate)) {
                newEnd = startDate;
              }
              setEndDate(newEnd);
            }}
            nextIcon={<IconChevronRight size={16} />}
            previousIcon={<IconChevronLeft size={16} />}
            renderDay={(dateStr) => {
              const dateObj = new Date(dateStr); // 문자열 → Date 객체
              const day = dateObj.getDay();

              const style: React.CSSProperties = {
                color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
              };

              return <div style={style}>{dateObj.getDate()}</div>;
            }}
          />
        </Flex>
      )}
      <div
        style={{
          flex: 1, // 남은 공간을 모두 차지
          minHeight: "300px", // 최소 높이 보장
          overflow: "auto",
          scrollbarGutter: "stable",
          boxSizing: "border-box",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          borderRadius: 8,
          background: "#fff",
          position: "relative",
        }}
      >
        <LoadingOverlay
          visible={isSearch}
          zIndex={1000}
          overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
          loaderProps={{ color: "pink", type: "bars" }}
        />
        <Table>
          <Table.Thead>
            <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
              <Table.Th p={12} pos={"sticky"} top={0} w={80}>
                <Checkbox
                  aria-label="Select all rows"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(event) =>
                    handleSelectAll(event.currentTarget.checked)
                  }
                />
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0} w={100}>
                번호
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0} w={150}>
                아이디
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0} w={150}>
                이름
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0} w={200}>
                권한
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0} w={120}>
                최근 로그인
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows}
            {/*Array.from({ length: 30 }).map((_, i) => (
                      <Table.Tr key={i} style={{ height: 30,}}>
                        <td colSpan={5}>asd</td>
                      </Table.Tr>
                    ))*/}
          </Table.Tbody>
        </Table>
      </div>
      <Flex pt={16} align={"flex-end"}>
        <Flex mr={"auto"} align={"center"}>
          <span style={{ marginRight: "1rem" }}>총 {total}개</span>
          <select
            onChange={(e) => {
              const opt_list = [15, 50, 100];
              setLength(opt_list[e.target.options.selectedIndex]);
              //getPageCount()
            }}
            style={{
              height: "31px",
              width: "50px",
              textAlign: "center",
              fontSize: "1rem",
            }}
          >
            <option>15</option>
            <option>50</option>
            <option>100</option>
          </select>
          <span>개씩 보기</span>
        </Flex>
        <ReactPaginate
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          pageCount={pageTotal}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(e) => {
            setPageNo(e.selected + 1);
          }}
          forcePage={pageNo - 1}
          containerClassName="pagination"
          activeClassName="active"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          nextClassName="page-item"
          previousLinkClassName="page-link"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
      </Flex>
    </div>
  );
};

export default Workers;
